import type { Static } from "@sinclair/typebox";
import type { TagCount } from "@volgo/core/contract";
import { type KeyboardEvent, type RefObject, useEffect, useId, useRef, useState } from "react";
import {
  addTag,
  applyTyped,
  menuConditionCount,
  normalizeTag,
  parseGid,
  type SearchQuery,
  splitTag,
} from "../search/query";
import { tokensOf } from "../search/tokens";
import { ConditionsMenu } from "./ConditionsMenu";
import { formatCount } from "./format";
import { Icon } from "./icons";
import { Token } from "./Token";

export type TagSuggestion = Static<typeof TagCount>;

const POLICY_MARK = { none: null, warn: "경고", block: "차단" } as const;

/**
 * The one search bar. Title text, required tags and excluded tags are typed here and become tokens;
 * conditions picked from the menu show up here as tokens too, so every condition is removed in one place.
 *
 * Keys: Enter adds what is typed (or the highlighted suggestion), Shift+Enter excludes it,
 * Backspace in an empty field removes the last token, the arrow keys walk the suggestions.
 */
export function SearchBar({
  query,
  onChange,
  suggest,
  delayMs = 150,
  inputRef,
  video = false,
}: {
  video?: boolean;
  query: SearchQuery;
  onChange: (next: SearchQuery) => void;
  suggest: (text: string) => Promise<TagSuggestion[]>;
  delayMs?: number;
  inputRef?: RefObject<HTMLInputElement | null>;
}) {
  const [typed, setTyped] = useState("");
  const [suggestions, setSuggestions] = useState<{ needle: string; items: TagSuggestion[] }>({
    needle: "",
    items: [],
  });
  const composing = useRef(false);
  const [active, setActive] = useState(-1);
  const [listOpen, setListOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const menuId = useId();

  const tokens = tokensOf(query);
  const excluding = typed.trimStart().startsWith("-");
  const needle = normalizeTag(typed.trimStart().replace(/^[+-]/, ""));
  const items = suggestions.needle === needle ? suggestions.items : [];
  const gid = excluding || video ? null : parseGid(typed.trim());
  const tagOffset = gid === null ? 0 : 1;
  const titleIndex = tagOffset + items.length;
  // the last row offers a title search; it makes no sense for `-something`
  const titleRow = needle !== "" && !/^[+-]/.test(typed.trimStart());
  const optionCount = titleIndex + (titleRow ? 1 : 0);
  const showList = listOpen && needle !== "" && optionCount > 0;

  useEffect(() => {
    if (showList && active >= 0) {
      document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: "nearest" });
    }
  }, [showList, active, listId]);

  useEffect(() => {
    if (!needle) {
      setSuggestions({ needle: "", items: [] });
      return;
    }
    let alive = true;
    const timer = setTimeout(() => {
      suggest(needle.slice(0, 100)).then(
        (found) => alive && setSuggestions({ needle, items: found }),
        () => alive && setSuggestions({ needle, items: [] }),
      );
    }, delayMs);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [needle, suggest, delayMs]);

  // the conditions menu closes on a click outside the search bar
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (e.target instanceof Node && !root.current?.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  function reset() {
    setTyped("");
    setSuggestions({ needle: "", items: [] });
    setActive(-1);
    setListOpen(false);
  }

  function commit(index: number, exclude: boolean) {
    const picked = items[index - tagOffset];
    let next: SearchQuery;
    if (gid !== null && index === 0) next = { ...query, gid };
    else if (picked) next = addTag(query, picked.tag, exclude || excluding ? "exclude" : "include");
    else if (titleRow && index === titleIndex) next = { ...query, text: typed.trim().slice(0, 200) };
    else if (exclude) next = addTag(query, typed.trimStart().replace(/^[+-]/, ""), "exclude");
    else next = applyTyped(query, typed.slice(0, 200));
    reset();
    if (next !== query) onChange(next);
  }

  function keyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (composing.current || e.nativeEvent.isComposing || e.keyCode === 229) {
      // Do not let IME confirmation also trigger the form's implicit submission.
      if (e.key === "Enter") e.preventDefault();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (typed.trim()) commit(active, e.shiftKey);
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (optionCount === 0) return;
      e.preventDefault();
      setListOpen(true);
      const step = e.key === "ArrowDown" ? 1 : -1;
      // -1 is "nothing highlighted": the field itself
      setActive((i) => ((i + 1 + step + optionCount + 1) % (optionCount + 1)) - 1);
    } else if (e.key === "Escape") {
      if (showList) {
        e.preventDefault();
        e.stopPropagation();
        setListOpen(false);
        setActive(-1);
      }
    } else if (e.key === "Backspace" && typed === "") {
      const last = tokens.at(-1);
      if (last) onChange(last.remove(query));
    }
  }

  const menuCount = menuConditionCount(query);

  return (
    <div className="find" ref={root}>
      {/* biome-ignore lint/a11y/useSemanticElements: native form submission supports mobile keyboard search actions */}
      <form
        className="search-form"
        role="search"
        aria-label="갤러리 검색"
        onSubmit={(e) => {
          e.preventDefault();
          if (!composing.current && typed.trim()) commit(active, false);
        }}
      >
        <div className="bar">
          <button type="submit" className="search-submit" aria-label="검색">
            <Icon name="search" small />
          </button>
          {tokens.map((t) => (
            <Token
              key={t.key}
              {...(t.namespace ? { namespace: t.namespace } : {})}
              excluded={t.kind === "xtag"}
              removeLabel={t.removeLabel}
              onRemove={() => onChange(t.remove(query))}
            >
              {t.label}
            </Token>
          ))}
          <input
            ref={inputRef}
            className="bar-input"
            type="text"
            enterKeyHint="search"
            role="combobox"
            aria-label="검색"
            aria-expanded={showList}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
            placeholder={
              video
                ? "영상·업로더·태그 찾기"
                : tokens.length
                  ? "태그·제목·GID 더하기"
                  : "태그·제목·GID로 찾기"
            }
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={200}
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
              setActive(-1);
              setListOpen(true);
            }}
            onFocus={() => setListOpen(true)}
            onBlur={() => setListOpen(false)}
            onCompositionStart={() => {
              composing.current = true;
            }}
            onCompositionEnd={() => {
              composing.current = false;
            }}
            onKeyDown={keyDown}
          />
          {video ? (
            <span className="bar-key" aria-hidden="true">
              <kbd>/</kbd>
            </span>
          ) : (
            <button
              ref={menuButton}
              type="button"
              className="cond"
              aria-expanded={menuOpen}
              aria-controls={menuId}
              onClick={() => setMenuOpen((v) => !v)}
            >
              조건{menuCount > 0 && <b>{menuCount}</b>}
            </button>
          )}
        </div>
      </form>

      {/* biome-ignore lint/a11y/noStaticElementInteractions: Esc closes the menu from any control inside it */}
      <div
        id={menuId}
        className="pop-anchor"
        hidden={!menuOpen}
        onKeyDown={(e) => {
          if (e.key !== "Escape") return;
          e.stopPropagation();
          setMenuOpen(false);
          menuButton.current?.focus();
        }}
      >
        {menuOpen && <ConditionsMenu query={query} onChange={onChange} />}
      </div>

      <div className="ac" hidden={!showList}>
        {/* focus stays in the field; rows are reached with aria-activedescendant (ARIA combobox pattern) */}
        <div id={listId} role="listbox" aria-label="검색 후보" tabIndex={-1}>
          {showList && gid !== null && (
            // biome-ignore lint/a11y/useKeyWithClickEvents: the keyboard picks rows from the field (arrow keys + Enter)
            <div
              id={`${listId}-0`}
              role="option"
              tabIndex={-1}
              aria-selected={active === 0}
              className={active === 0 ? "ac-row ac-on" : "ac-row"}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => commit(0, false)}
            >
              <div>GID: {gid}</div>
            </div>
          )}
          {showList &&
            items.map((item, i) => {
              const index = i + tagOffset;
              const { namespace, name } = splitTag(item.tag);
              const mark = POLICY_MARK[item.policy];
              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: the keyboard picks rows from the field (arrow keys + Enter)
                <div
                  key={item.tag}
                  id={`${listId}-${index}`}
                  role="option"
                  tabIndex={-1}
                  aria-selected={index === active}
                  className={index === active ? "ac-row ac-on" : "ac-row"}
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={(e) => commit(index, e.shiftKey)}
                >
                  <div>
                    {namespace && <i>{namespace}:</i>}
                    {name}
                  </div>
                  {mark && <em>{mark}</em>}
                  <span>{formatCount(item.count)}</span>
                </div>
              );
            })}
          {showList && titleRow && (
            // biome-ignore lint/a11y/useKeyWithClickEvents: the keyboard picks rows from the field (arrow keys + Enter)
            <div
              id={`${listId}-${titleIndex}`}
              role="option"
              tabIndex={-1}
              aria-selected={active === titleIndex}
              className={active === titleIndex ? "ac-row ac-on" : "ac-row"}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => commit(titleIndex, false)}
            >
              <div>
                제목에 “{typed.trim()}”가 들어간 {video ? "영상 찾기" : "갤러리 찾기"}
              </div>
            </div>
          )}
        </div>
        <p>
          <span>
            <kbd>Enter</kbd> 포함
          </span>
          <span>
            <kbd>Shift</kbd> <kbd>Enter</kbd> 제외
          </span>
          <span>
            <kbd>Backspace</kbd> 앞 토큰 지우기
          </span>
        </p>
      </div>
    </div>
  );
}
